/* ════ IDEA MODAL ══════════════════════════════════════════ */
import { state } from '../state.js';
import { $, $$ } from '../dom.js';
import { sb } from '../supabase/client.js';
import { setSyncDot, logActivity } from '../supabase/data.js';
import { showToast } from '../utils/toast.js';
import { refreshIdeaStUI } from '../subtasks.js';
import { refreshIdeaTags } from '../tagsedit.js';
import { displayName } from '../utils/name.js';
import { canSeeIdea } from '../privacy.js';

// New ideas land in whichever inbox you're viewing (your own unless you've
// switched the picker to a specific teammate).
const inboxOwner = () => (state.inboxUser && state.inboxUser !== 'all') ? state.inboxUser : (state.currentUser?.email || null);

const nameFor = email => state.resources.find(r => r.user_email === email)?.name
  || state.users.find(u => u.email === email)?.name || displayName(email);

const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// Every known login email (registry + resources + authors).
function knownEmails() {
  const me = state.currentUser?.email || '';
  return [...new Set([...state.users.map(u => u.email), ...state.resources.map(r => r.user_email),
    ...state.ideas.map(i => i.created_by), ...state.tasks.map(t => t.created_by), me].filter(Boolean))].sort();
}

// "Flag for review" chips + an add picker, backed by state.ideaMentions.
export function renderIdeaMentions() {
  const list = $('idea-mentions-list'); if (!list) return;
  list.innerHTML = state.ideaMentions.map(e =>
    `<span class="mention-chip">🔔 ${esc(nameFor(e))}<button class="mention-x" data-e="${esc(e)}" title="Remove">×</button></span>`).join('');
  list.querySelectorAll('.mention-x').forEach(b => b.addEventListener('click', () => {
    state.ideaMentions = state.ideaMentions.filter(x => x !== b.dataset.e); renderIdeaMentions();
  }));
  const sel = $('idea-mention-add');
  const taken = new Set(state.ideaMentions);
  const opts = knownEmails().filter(e => !taken.has(e));
  sel.innerHTML = '<option value="">+ Flag someone to review…</option>' +
    opts.map(e => `<option value="${esc(e)}">${esc(nameFor(e))}</option>`).join('');
  sel.onchange = () => { if (sel.value && !taken.has(sel.value)) { state.ideaMentions.push(sel.value); renderIdeaMentions(); } };
}

// Populate the "Inbox" owner select with every known user.
function fillOwner(selected) {
  const me = state.currentUser?.email || '';
  const emails = [...new Set([...state.users.map(u => u.email), ...state.resources.map(r => r.user_email),
    ...state.ideas.map(i => i.created_by), ...state.tasks.map(t => t.created_by), me, selected].filter(Boolean))].sort();
  $('idea-owner').innerHTML = emails.map(e =>
    `<option value="${e}"${e === selected ? ' selected' : ''}>${nameFor(e)}${e === me ? ' (me)' : ''}</option>`).join('');
}

export function pickIdeaCat(el) { $$('#idea-cat-row .cat-chip').forEach(c => c.classList.remove('sel')); el.classList.add('sel'); state.selIdeaCat = el.dataset.c; }
function fillIdeaRes(sel) {
  $('idea-res').innerHTML = `<option value="">— Unassigned —</option>` +
    state.resources.filter(r => r.id !== 'unassigned').map(r => `<option value="${r.id}"${r.id === sel ? ' selected' : ''}>${r.name}</option>`).join('');
}

export function openIdeaModal() {
  state.editIdeaId = null; state.ideaSubtasks = [];
  const owner = inboxOwner();
  const elsewhere = owner && owner !== state.currentUser?.email;
  $('idea-m-title').textContent = elsewhere ? `New idea — ${displayName(owner)}'s inbox` : 'New idea';
  $('idea-del-btn').style.display = 'none';
  $('idea-name').value = '';
  $('idea-desc').value = '';
  $('idea-days').value = '';
  $('idea-due').value = '';
  $('idea-imp').value = 'normal'; state.selIdeaImp = 'normal'; $('idea-lock').checked = false; $('idea-private').checked = false;
  fillOwner(owner || '');
  state.ideaMentions = []; renderIdeaMentions();
  state.ideaTags = []; refreshIdeaTags();
  fillIdeaRes('');
  state.selIdeaCat = 'output';
  $$('#idea-cat-row .cat-chip').forEach(c => c.classList.remove('sel'));
  document.querySelector('#idea-cat-row .cat-chip[data-c="output"]').classList.add('sel');
  refreshIdeaStUI();
  $('idea-modal').classList.add('open');
  setTimeout(() => $('idea-name').focus(), 240);
}

export function openIdeaEdit(id) {
  const idea = state.ideas.find(i => i.id === id); if (!idea) return;
  if (!canSeeIdea(idea)) { showToast('🔒 This idea is private'); return; }
  state.editIdeaId = id; state.ideaSubtasks = [...(idea.subtasks || [])];
  $('idea-m-title').textContent = 'Edit idea';
  $('idea-del-btn').style.display = 'inline-flex';
  $('idea-name').value = idea.name;
  $('idea-desc').value = idea.description || '';
  $('idea-days').value = idea.days || '';
  $('idea-due').value = idea.due || '';
  $('idea-imp').value = idea.importance || 'normal'; state.selIdeaImp = idea.importance || 'normal'; $('idea-lock').checked = !!idea.locked; $('idea-private').checked = !!idea.private;
  fillOwner(idea.created_by || '');
  state.ideaMentions = [...(idea.mentions || [])]; renderIdeaMentions();
  state.ideaTags = [...(idea.tags || [])]; refreshIdeaTags();
  fillIdeaRes(idea.resource || '');
  state.selIdeaCat = idea.cat || 'output';
  $$('#idea-cat-row .cat-chip').forEach(c => c.classList.remove('sel'));
  document.querySelector(`#idea-cat-row .cat-chip[data-c="${state.selIdeaCat}"]`)?.classList.add('sel');
  refreshIdeaStUI();
  $('idea-modal').classList.add('open');
}

export function closeIdeaModal() { $('idea-modal').classList.remove('open'); state.editIdeaId = null; }

export async function saveIdea() {
  const name = $('idea-name').value.trim(); if (!name) { showToast('Please enter a title'); return; }
  const data = {
    name, description: $('idea-desc').value, cat: state.selIdeaCat,
    resource: $('idea-res').value || null,
    days: parseFloat($('idea-days').value) || null,
    due: $('idea-due').value || null,
    importance: $('idea-imp').value, locked: $('idea-lock').checked, private: $('idea-private').checked,
    tags: state.ideaTags, mentions: state.ideaMentions,
    subtasks: state.ideaSubtasks,
  };
  setSyncDot('busy'); let error;
  // The Inbox owner select drives which inbox the card lives in (move support).
  data.created_by = $('idea-owner').value || inboxOwner();
  if (state.editIdeaId) { ({ error } = await sb.from('ideas').update(data).eq('id', state.editIdeaId)); }
  else { ({ error } = await sb.from('ideas').insert(data)); }
  if (error) { setSyncDot('err'); showToast('Save failed: ' + error.message); return; }
  setSyncDot('ok'); showToast(state.editIdeaId ? 'Idea updated ✓' : 'Idea added ✓');
  logActivity(state.editIdeaId ? 'updated' : 'added', 'idea', name);
  closeIdeaModal();
}

export async function deleteIdea() {
  if (state.editIdeaId === null || !confirm('Delete this idea?')) return;
  const nm = state.ideas.find(i => i.id === state.editIdeaId)?.name || '';
  setSyncDot('busy'); const { error } = await sb.from('ideas').delete().eq('id', state.editIdeaId);
  if (error) { setSyncDot('err'); showToast('Failed: ' + error.message); return; }
  setSyncDot('ok'); showToast('Idea deleted'); logActivity('deleted', 'idea', nm); closeIdeaModal();
}
